import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Heart, Gift, Users, TrendingUp, Award, Target, Calendar,
  ArrowRight, History, Loader2, Star, Zap
} from 'lucide-react';
import { doadorApiCall } from './DoadorPortalLayout';
import { toast } from 'sonner';

export default function DoadorDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await doadorApiCall('/doador/dashboard');
      setDashboard(data);
    } catch (error) {
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Erro ao carregar dados</p>
        <Button onClick={loadDashboard} className="mt-4">Tentar novamente</Button>
      </div>
    );
  }

  const { estatisticas, impacto, badges, proximo_nivel, campanhas_ativas, ultimas_atividades } = dashboard;

  return (
    <div className="space-y-6" data-testid="doador-dashboard">
      {/* Welcome Banner */}
      <Card className="bg-gradient-to-r from-rose-500 to-amber-500 text-white border-0 overflow-hidden">
        <CardContent className="pt-6 relative">
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
          <div className="relative">
            <h1 className="text-2xl font-bold mb-2">
              Olá, {dashboard.doador?.nome?.split(' ')[0]}! 👋
            </h1>
            <p className="text-rose-100">
              O seu impacto faz a diferença. Obrigado por fazer parte da nossa comunidade de doadores.
            </p>
            <div className="flex gap-4 mt-4">
              <Badge className="bg-white/20 hover:bg-white/30 text-white">
                <Gift className="w-3 h-3 mr-1" />
                {estatisticas?.pontos || 0} pontos
              </Badge>
              <Badge className="bg-white/20 hover:bg-white/30 text-white">
                <Award className="w-3 h-3 mr-1" />
                {badges?.length || 0} badges
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white hover:shadow-md transition-shadow">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Doações</p>
                <p className="text-2xl font-bold text-slate-900">{estatisticas?.total_doacoes || 0}</p>
              </div>
              <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-rose-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white hover:shadow-md transition-shadow">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Valor Total</p>
                <p className="text-2xl font-bold text-slate-900">
                  {(estatisticas?.valor_total_doado || 0).toLocaleString('pt-PT')}€
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white hover:shadow-md transition-shadow">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Famílias Ajudadas</p>
                <p className="text-2xl font-bold text-slate-900">{impacto?.familias_ajudadas || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white hover:shadow-md transition-shadow">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pontos</p>
                <p className="text-2xl font-bold text-slate-900">{estatisticas?.pontos || 0}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Gift className="w-6 h-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Badges & Progress */}
        <div className="lg:col-span-2 space-y-6">
          {/* Badges */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-500" />
                  Seus Badges
                </CardTitle>
                <Link to="/doador/pontos">
                  <Button variant="ghost" size="sm">
                    Ver todos <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {badges && badges.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {badges.slice(0, 6).map((badge, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-50 to-rose-50 rounded-lg border border-amber-200"
                    >
                      <span className="text-xl">{badge.icone}</span>
                      <span className="text-sm font-medium text-slate-700">{badge.nome}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">Ainda não tem badges. Continue a doar!</p>
              )}
              
              {proximo_nivel && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Próximo nível: {proximo_nivel.nome}</span>
                    <span className="font-medium">{estatisticas?.pontos || 0}/{proximo_nivel.pontos_necessarios}</span>
                  </div>
                  <Progress 
                    value={(estatisticas?.pontos / proximo_nivel.pontos_necessarios) * 100} 
                    className="h-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Faltam {proximo_nivel.pontos_faltam} pontos
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Impacto */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Star className="w-5 h-5 text-rose-500" />
                Seu Impacto em Números
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-rose-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-rose-600">{impacto?.produtos_doados || 0}</p>
                  <p className="text-sm text-rose-700">Produtos Doados</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-blue-600">{impacto?.horas_voluntariado || 0}h</p>
                  <p className="text-sm text-blue-700">Voluntariado</p>
                </div>
                <div className="p-4 bg-green-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-green-600">
                    {(impacto?.valor_financeiro || 0).toLocaleString('pt-PT')}€
                  </p>
                  <p className="text-sm text-green-700">Doações Financeiras</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-amber-600">{impacto?.familias_ajudadas || 0}</p>
                  <p className="text-sm text-amber-700">Famílias Apoiadas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Campaigns & Activity */}
        <div className="space-y-6">
          {/* Campanhas Ativas */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-500" />
                  Campanhas Ativas
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {campanhas_ativas && campanhas_ativas.length > 0 ? (
                <div className="space-y-3">
                  {campanhas_ativas.slice(0, 3).map((camp, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                      <p className="font-medium text-sm text-slate-900">{camp.nome}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{camp.tipo}</Badge>
                        {camp.data_fim && (
                          <span className="text-xs text-slate-500">
                            até {new Date(camp.data_fim).toLocaleDateString('pt-PT')}
                          </span>
                        )}
                      </div>
                      {camp.meta && (
                        <div className="mt-2">
                          <Progress 
                            value={(camp.total_angariado / camp.meta) * 100} 
                            className="h-1.5"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            {camp.total_angariado?.toLocaleString('pt-PT')}€ / {camp.meta?.toLocaleString('pt-PT')}€
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4 text-sm">Sem campanhas ativas</p>
              )}
              <Link to="/doador/campanhas">
                <Button variant="outline" className="w-full mt-3" size="sm">
                  Ver todas as campanhas
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Atividade Recente */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-slate-500" />
                Atividade Recente
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ultimas_atividades && ultimas_atividades.length > 0 ? (
                <div className="space-y-3">
                  {ultimas_atividades.map((ativ, idx) => (
                    <div key={idx} className="flex items-start gap-3 text-sm">
                      <div className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Zap className="w-4 h-4 text-rose-500" />
                      </div>
                      <div>
                        <p className="text-slate-700">{ativ.descricao}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(ativ.created_at).toLocaleDateString('pt-PT')}
                        </p>
                        {ativ.pontos_ganhos > 0 && (
                          <Badge className="mt-1 bg-amber-100 text-amber-700 text-xs">
                            +{ativ.pontos_ganhos} pontos
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4 text-sm">Sem atividade recente</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
